import { IndicatorData } from '../TechnicalAnalysisTradingView';
import { OhlcRow } from './SMA';
import { ELEMENT_INFO } from './DailyElement';

const stemsCN = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"] as const;
const branchesCN = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"] as const;

const STEM_ELEMENT: Record<string, { element: string; yy: "Yin"|"Yang" }> = {
  "甲":{element:"Wood",yy:"Yang"},"乙":{element:"Wood",yy:"Yin"},
  "丙":{element:"Fire",yy:"Yang"},"丁":{element:"Fire",yy:"Yin"},
  "戊":{element:"Earth",yy:"Yang"},"己":{element:"Earth",yy:"Yin"},
  "庚":{element:"Metal",yy:"Yang"},"辛":{element:"Metal",yy:"Yin"},
  "壬":{element:"Water",yy:"Yang"},"癸":{element:"Water",yy:"Yin"},
};

const BRANCH_META_FULL: Record<string,{ shio:string; element:string; yy:"Yin"|"Yang"}> = {
  "子":{shio:"Rat",   element:"Water",yy:"Yang"},
  "丑":{shio:"Ox",    element:"Earth",yy:"Yin"},
  "寅":{shio:"Tiger", element:"Wood", yy:"Yang"},
  "卯":{shio:"Rabbit",element:"Wood", yy:"Yin"},
  "辰":{shio:"Dragon",element:"Earth",yy:"Yang"},
  "巳":{shio:"Snake", element:"Fire", yy:"Yin"},
  "午":{shio:"Horse", element:"Fire", yy:"Yang"},
  "未":{shio:"Goat",  element:"Earth",yy:"Yin"},
  "申":{shio:"Monkey",element:"Metal",yy:"Yang"},
  "酉":{shio:"Rooster",element:"Metal",yy:"Yin"},
  "戌":{shio:"Dog",   element:"Earth",yy:"Yang"},
  "亥":{shio:"Pig",   element:"Water",yy:"Yin"},
};

// Urutan branch bulan mulai Li Chun (寅)
const MONTH_BRANCHES_CN = ["寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥", "子", "丑"] as const;
const MONTH_STEM_MAP: Record<string, readonly string[]> = {
  "甲": ["丙", "丁", "戊", "己", "庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁"],
  "乙": ["戊", "己", "庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁", "戊", "己"],
  "丙": ["庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁", "戊", "己", "庚", "辛"],
  "丁": ["壬", "癸", "甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"],
  "戊": ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸", "甲", "乙"],
  "己": ["丙", "丁", "戊", "己", "庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁"],
  "庚": ["戊", "己", "庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁", "戊", "己"],
  "辛": ["庚", "辛", "壬", "癸", "甲", "乙", "丙", "丁", "戊", "己", "庚", "辛"],
  "壬": ["壬", "癸", "甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"],
  "癸": ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸", "甲", "乙"],
};

const STEM_ANCHOR_UTC = new Date(Date.UTC(1900, 0, 30, 0, 0, 0)); // 15 Sep = Air Yang, 16 Sep = Air Yin
const BRANCH_ANCHOR_UTC = new Date(Date.UTC(1900, 0, 26, 0, 0, 0)); // 16 Sep = Ular Api Yin


function normalize360(v: number): number {
  let x = v % 360;
  if (x < 0) x += 360;
  return x;
}

function toJulianDayUTC(dt: Date): number {
  const y = dt.getUTCFullYear(); let m = dt.getUTCMonth()+1;
  const D = dt.getUTCDate() + (dt.getUTCHours() + (dt.getUTCMinutes() + dt.getUTCSeconds()/60)/60)/24;
  let Y = y; if (m<=2){ Y=y-1; m+=12; }
  const A = Math.floor(Y/100); const B = 2 - A + Math.floor(A/4);
  return Math.floor(365.25*(Y+4716)) + Math.floor(30.6001*(m+1)) + D + B - 1524.5;
}

function solarAppLongitudeDeg(d: Date): number {
  const JD = toJulianDayUTC(d);
  const T = (JD - 2451545.0) / 36525.0;
  const L0 = normalize360(280.46646 + 36000.76983 * T);
  const M = normalize360(357.52911 + 35999.05029 * T);
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M * Math.PI / 180)
    + (0.019993 - 0.000101 * T) * Math.sin(2 * M * Math.PI / 180)
    + 0.000289 * Math.sin(3 * M * Math.PI / 180);
  const trueLong = L0 + C;
  const omega = normalize360(125.04 - 1934.136 * T);
  const lam = trueLong - 0.00569 - 0.00478 * Math.sin(omega * Math.PI / 180);
  return normalize360(lam);
}

function monthIndexFromLongitude(lam: number): number {
  const d = normalize360(lam - 315);
  return Math.floor(d / 30) % 12; // 0..11
}

function getBaZiYearAndMonth(date: Date): { year: number; month: number } {
  const evalDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 15, 50, 0));
  const lam = solarAppLongitudeDeg(evalDate);
  const calYear = evalDate.getUTCFullYear();
  const calMonth = evalDate.getUTCMonth(); // 0..11
  
  let solarYear = calYear;
  if (calMonth === 0) {
    solarYear = calYear - 1;
  } else if (calMonth === 1) {
    if (lam < 315) {
      solarYear = calYear - 1;
    }
  }
  
  const monthIndex = monthIndexFromLongitude(lam); // 0..11
  return { year: solarYear, month: monthIndex };
}

export const ELEMENT_COLORS: Record<string, string> = {
  "Wood": "#16a34a",    // Green (🌳)
  "Fire": "#ff4500",    // Fiery Orange-Red (🔥)
  "Earth": "#6b4226",   // Deep Natural Soil Brown (⛰️)
  "Metal": "#94a3b8",   // Metallic Steel Silver (⚒️)
  "Water": "#38bdf8"    // Light Sky Blue (🌊)
};

export const ELEMENT_NAMES_ID: Record<string, string> = {
  "Wood": "Kayu (Wood 🌳)",
  "Fire": "Api (Fire 🔥)",
  "Earth": "Tanah (Earth ⛰️)",
  "Metal": "Logam (Metal ⚒️)",
  "Water": "Air (Water 🌊)"
};

export const ELEMENT_SIMPLE_ID: Record<string, string> = {
  "Wood": "Kayu",
  "Fire": "Api",
  "Earth": "Tanah",
  "Metal": "Logam",
  "Water": "Air"
};

export const SHIO_NAMES_ID: Record<string, { id: string; emoji: string }> = {
  "Rat": { id: "Tikus", emoji: "🐭" },
  "Ox": { id: "Kerbau", emoji: "🐂" },
  "Tiger": { id: "Macan", emoji: "🐅" },
  "Rabbit": { id: "Kelinci", emoji: "🐇" },
  "Dragon": { id: "Naga", emoji: "🐉" },
  "Snake": { id: "Ular", emoji: "🐍" },
  "Horse": { id: "Kuda", emoji: "🐎" },
  "Goat": { id: "Kambing", emoji: "🐐" },
  "Monkey": { id: "Monyet", emoji: "🐒" },
  "Rooster": { id: "Ayam", emoji: "🐓" },
  "Dog": { id: "Keledai", emoji: "🫏" },
  "Pig": { id: "Babi", emoji: "🐖" }
};

const baziDetailCache = new Map<string, any>();

export function getDayBaZiDetail(date: Date, timeframe?: string, mode?: 'daily' | 'monthly' | string) {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const cacheKey = `${y}-${m}-${d}_${timeframe || ''}_${mode || ''}`;

  const cached = baziDetailCache.get(cacheKey);
  if (cached) return cached;

  let pair = '甲子';
  let stem = '甲';
  let branch = '子';

  const isMonthly = mode === 'monthly';

  if (isMonthly) {
    // BaZi Monthly / Bi-Weekly pillar
    const { year: solarYear, month: monthIdx } = getBaZiYearAndMonth(date);
    const yearStemCN = stemsCN[((solarYear - 1984) % 10 + 10) % 10] || '甲';
    const monthBranchCN = MONTH_BRANCHES_CN[monthIdx] || '寅';
    const monthStemCN = MONTH_STEM_MAP[yearStemCN]?.[monthIdx] || '丙';
    stem = monthStemCN;
    branch = monthBranchCN;
    pair = stem + branch;
  } else if (timeframe === '1Y') {
    // BaZi Yearly pillar
    const { year: solarYear } = getBaZiYearAndMonth(date);
    const yearStemCN = stemsCN[((solarYear - 1984) % 10 + 10) % 10] || '甲';
    const yearBranchCN = branchesCN[((solarYear - 1984) % 12 + 12) % 12] || '子';
    stem = yearStemCN;
    branch = yearBranchCN;
    pair = stem + branch;
  } else {
    // BaZi Daily pillar
    const dayStartUTC = new Date(Date.UTC(y, m, d, 0, 0, 0));
    const JD = toJulianDayUTC(dayStartUTC); 

    const JD_stem = toJulianDayUTC(STEM_ANCHOR_UTC);
    const delta_stem = Math.round(JD - JD_stem);
    const stemIdx = ((delta_stem % 10) + 10) % 10;
    stem = stemsCN[stemIdx] || '甲';

    const JD_branch = toJulianDayUTC(BRANCH_ANCHOR_UTC);
    const delta_branch = Math.round(JD - JD_branch);
    const branchIdx = ((delta_branch % 12) + 12) % 12;
    branch = branchesCN[branchIdx] || '子';

    pair = stem + branch;
  }

  const se = STEM_ELEMENT[stem]; 
  const bm = BRANCH_META_FULL[branch];

  const mainElement = se?.element || 'Wood';
  const mainYinYang = se?.yy || 'Yang';
  const mainYYCode: "A" | "I" = mainYinYang === 'Yang' ? 'A' : 'I';

  const shio = bm?.shio || 'Rat';
  const shioElement = bm?.element || 'Water';
  const shioYinYang = bm?.yy || 'Yang';
  const shioYYCode: "A" | "I" = shioYinYang === 'Yang' ? 'A' : 'I';

  const baseMainSimple = ELEMENT_SIMPLE_ID[mainElement] || mainElement;
  const mainElementName = `${ELEMENT_NAMES_ID[mainElement] || mainElement} (${mainYYCode})`;
  const mainElementSimple = `${baseMainSimple} (${mainYYCode})`;
  const shioInfo = SHIO_NAMES_ID[shio] || { id: shio, emoji: '🐾' };
  const shioSimple = shioInfo.id || shio;
  const shioName = `${shioInfo.id} ${shioInfo.emoji}`;
  const shioElementName = `${ELEMENT_NAMES_ID[shioElement] || shioElement} (${shioYYCode})`;
  const shioElementSimple = (ELEMENT_SIMPLE_ID[shioElement] || shioElement).toLowerCase();
  const shioCombinedText = `${shioSimple} (${shioElementSimple} ${shioYYCode})`;

  const res = {
    pillarCN: pair,
    stemCN: stem,
    branchCN: branch,
    mainElement,
    mainYinYang,
    mainYYCode,
    mainElementName,
    mainElementSimple,
    mainElementColor: ELEMENT_COLORS[mainElement] || '#16a34a',
    shio,
    shioSimple,
    shioName,
    shioElement,
    shioYinYang,
    shioYYCode,
    shioElementName,
    shioElementSimple,
    shioCombinedText,
    shioElementColor: ELEMENT_COLORS[shioElement] || '#38bdf8',
  };

  if (baziDetailCache.size > 1000) {
    baziDetailCache.clear();
  }
  baziDetailCache.set(cacheKey, res);
  return res;
}

export interface BaZiTextItem {
  time: number;
  upperText: string;
  lowerText: string;
}

class BaZiTextRenderer {
  private _primitive: BaZiTextPrimitive;

  constructor(primitive: BaZiTextPrimitive) {
    this._primitive = primitive;
  }

  draw(target: any) {
    this._drawImpl(target);
  }

  drawBackground() {
    // Empty: foreground rendering handled in draw()
  }

  private _drawImpl(target: any) {
    const chart = this._primitive.chart;
    const series = this._primitive.series;
    const items = this._primitive.items;
    if (!chart || !series || !items || !items.length) return;

    target.useMediaCoordinateSpace((scope: any) => {
      const ctx: CanvasRenderingContext2D = scope.context;
      const timeScale = chart.timeScale();
      const yUpper = series.priceToCoordinate(0.75);
      const yLower = series.priceToCoordinate(0.25);
      if (yUpper === null || yLower === null) return;

      ctx.save();
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;
        const x = timeScale.timeToCoordinate(item.time as any);
        if (x === null || x === undefined || x < -20 || x > ctx.canvas.width + 20) continue;

        if (item.upperText) {
          ctx.fillText(item.upperText, x, yUpper);
        }
        if (item.lowerText) {
          ctx.fillText(item.lowerText, x, yLower);
        }
      }

      ctx.restore();
    });
  }
}

class BaZiTextPaneView {
  private _renderer: BaZiTextRenderer;

  constructor(primitive: BaZiTextPrimitive) {
    this._renderer = new BaZiTextRenderer(primitive);
  }

  zOrder(): 'top' {
    return 'top';
  }

  renderer() {
    return this._renderer;
  }
}

export class BaZiTextPrimitive {
  public chart: any = null;
  public series: any = null;
  public items: BaZiTextItem[] = [];
  private _paneViews: BaZiTextPaneView[];
  private _requestUpdate: (() => void) | null = null;

  constructor(items: BaZiTextItem[]) {
    this.items = items;
    this._paneViews = [new BaZiTextPaneView(this)];
  }

  setData(items: BaZiTextItem[]) {
    this.items = items;
    if (this._requestUpdate) this._requestUpdate();
  }

  attached(param: any) {
    this.chart = param.chart;
    this.series = param.series;
    this._requestUpdate = param.requestUpdate;
    if (this._requestUpdate) this._requestUpdate();
  }

  detached() {
    this.chart = null;
    this.series = null;
    this._requestUpdate = null;
  }

  updateAllViews() {}

  paneViews() {
    return this._paneViews;
  }
}

export function calculateBaZiIndicator(data: OhlcRow[], timeframe?: string, mode?: 'daily' | 'monthly' | string): {
  data: (IndicatorData & { color: string })[];
  mainElementData: (IndicatorData & { color: string })[];
  shioElementData: (IndicatorData & { color: string })[];
  textItems: BaZiTextItem[];
} {
  const mainElementData: (IndicatorData & { color: string })[] = [];
  const shioElementData: (IndicatorData & { color: string })[] = [];
  const textItems: BaZiTextItem[] = [];

  if (!data || data.length === 0) {
    return { data: mainElementData, mainElementData, shioElementData, textItems };
  }

  const effectiveMode = mode === 'monthly' ? 'monthly' : 'daily';

  // 1. Fill data from existing chart rows
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (row) {
      const date = new Date(row.time * 1000);
      const bazi = getDayBaZiDetail(date, timeframe, effectiveMode);

      mainElementData.push({
        time: row.time,
        value: 1.0, // Top layer (Element Utama): spans 0 to 1.0
        color: bazi.mainElementColor,
      });

      shioElementData.push({
        time: row.time,
        value: 0.5, // Bottom layer (Element Shio): spans 0 to 0.5
        color: bazi.shioElementColor,
      });

      textItems.push({
        time: row.time,
        upperText: bazi.mainYYCode,
        lowerText: "",
      });
    }
  }

  // 2. Pad future dates up to December 31 of the latest year (or current year)
  const lastRow = data[data.length - 1];
  if (lastRow) {
    const lastDate = new Date(lastRow.time * 1000);
    const currentYear = new Date().getUTCFullYear();
    const targetYear = Math.max(lastDate.getUTCFullYear(), currentYear);
    const endOfYearSec = Math.floor(Date.UTC(targetYear, 11, 31, 23, 59, 59) / 1000);

    if (timeframe === '1MO' || timeframe === '1M_BAZI' || timeframe === '3M' || timeframe === '6M' || timeframe === '1Y') {
      let nextDate = new Date(Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth() + 1, 15, 0, 0, 0));
      while (Math.floor(nextDate.getTime() / 1000) <= endOfYearSec) {
        const tSec = Math.floor(nextDate.getTime() / 1000);
        const bazi = getDayBaZiDetail(nextDate, timeframe, effectiveMode);

        mainElementData.push({ time: tSec, value: 1.0, color: bazi.mainElementColor });
        shioElementData.push({ time: tSec, value: 0.5, color: bazi.shioElementColor });
        textItems.push({ time: tSec, upperText: bazi.mainYYCode, lowerText: "" });

        nextDate = new Date(Date.UTC(nextDate.getUTCFullYear(), nextDate.getUTCMonth() + 1, 15, 0, 0, 0));
      }
    } else if (timeframe === '2W_BAZI') {
      let nextDate = new Date(lastDate.getTime() + 15 * 86400 * 1000);
      while (Math.floor(nextDate.getTime() / 1000) <= endOfYearSec) {
        const tSec = Math.floor(nextDate.getTime() / 1000);
        const bazi = getDayBaZiDetail(nextDate, timeframe, effectiveMode);

        mainElementData.push({ time: tSec, value: 1.0, color: bazi.mainElementColor });
        shioElementData.push({ time: tSec, value: 0.5, color: bazi.shioElementColor });
        textItems.push({ time: tSec, upperText: bazi.mainYYCode, lowerText: "" });

        nextDate = new Date(nextDate.getTime() + 15 * 86400 * 1000);
      }
    } else {
      const SECONDS_PER_DAY = 86400;
      let nextTimeSec = lastRow.time + SECONDS_PER_DAY;

      while (nextTimeSec <= endOfYearSec) {
        const nextDate = new Date(nextTimeSec * 1000);
        const bazi = getDayBaZiDetail(nextDate, timeframe, effectiveMode);

        mainElementData.push({ time: nextTimeSec, value: 1.0, color: bazi.mainElementColor });
        shioElementData.push({ time: nextTimeSec, value: 0.5, color: bazi.shioElementColor });
        textItems.push({ time: nextTimeSec, upperText: bazi.mainYYCode, lowerText: "" });

        nextTimeSec += SECONDS_PER_DAY;
      }
    }
  }

  return { data: mainElementData, mainElementData, shioElementData, textItems };
}

export { ELEMENT_INFO };

